// FILE: SnoozedThreadsView.tsx
// Purpose: The list of threads this person has put aside, with what is left on each and the
//          two ways out of it.
// Layer: View
// Exports: SnoozedThreadsView

import { useNavigate } from "@tanstack/react-router";
import { AlarmClockIcon, ArchiveIcon, Undo2Icon } from "~/lib/icons";
import { useEffect, useMemo, useState } from "react";

import { useAuthStore } from "../../auth/authStore";
import {
  formatSnoozeCountdown,
  formatSnoozeDeadline,
  isThreadSnoozed,
  nextSnoozeExpiryMs,
} from "../../lib/threadInboxState";
import { setThreadSnoozed } from "../../lib/threadInboxActions";
import { newCommandId } from "../../lib/utils";
import { readNativeApi } from "../../nativeApi";
import { useStore } from "../../store";
import { createSidebarDisplayThreadsSelector } from "../../storeSelectors";
import type { ThreadId } from "@taskin-code/contracts";
import type { ProjectId } from "@taskin-code/contracts";
import { Button } from "../ui/button";
import { SidebarHeaderTrigger, SidebarInset } from "../ui/sidebar";
import { toastManager } from "../ui/toast";

/**
 * How often the countdowns are recomputed while nothing else changes.
 *
 * The rows show minutes, so a minute is the finest granularity worth redrawing.
 * The moment a snooze actually runs out is handled separately and exactly — see
 * the expiry timer below — because that one has to be prompt, not approximate.
 */
const COUNTDOWN_TICK_MS = 60_000;

export function SnoozedThreadsView(props: { fixedProjectId?: ProjectId } = {}) {
  const navigate = useNavigate();
  const selectSidebarDisplayThreads = useMemo(() => createSidebarDisplayThreadsSelector(), []);
  const threads = useStore(selectSidebarDisplayThreads);
  const projects = useStore((store) => store.projects);
  const projectAssignments = useAuthStore((state) => state.projectAssignments);
  const [now, setNow] = useState(() => Date.now());

  const snoozed = useMemo(
    () =>
      threads
        .filter(
          (thread) =>
            isThreadSnoozed(thread, now) &&
            (props.fixedProjectId === undefined || thread.projectId === props.fixedProjectId),
        )
        .toSorted((left, right) =>
          (left.snoozedUntil ?? "").localeCompare(right.snoozedUntil ?? ""),
        ),
    [now, props.fixedProjectId, threads],
  );

  // Two clocks, on purpose. This one keeps the countdowns honest while the page
  // sits open...
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), COUNTDOWN_TICK_MS);
    return () => window.clearInterval(timer);
  }, []);

  // ...and this one fires on the exact instant the next snooze ends, so the row
  // leaves the list then rather than up to a minute later.
  const nextExpiryMs = nextSnoozeExpiryMs(snoozed, now);
  useEffect(() => {
    if (nextExpiryMs === null) return;
    const delay = Math.max(0, nextExpiryMs - Date.now()) + 1;
    const timer = window.setTimeout(() => setNow(Date.now()), delay);
    return () => window.clearTimeout(timer);
  }, [nextExpiryMs]);

  const projectNameById = useMemo(
    () => new Map(projects.map((project) => [project.id, project.name])),
    [projects],
  );

  const wake = async (threadId: ThreadId, snoozedUntil: string | null) => {
    const api = readNativeApi();
    if (!api) return;
    await setThreadSnoozed({
      api: api.orchestration,
      store: useStore.getState(),
      threadId,
      snoozeUntil: null,
      previousSnoozedUntil: snoozedUntil,
    }).catch(() => {
      toastManager.add({ type: "error", title: "No se pudo devolver el hilo a la lista" });
    });
  };

  const archive = async (threadId: ThreadId) => {
    const api = readNativeApi();
    if (!api) return;
    try {
      await api.orchestration.dispatchCommand({
        type: "thread.archive",
        commandId: newCommandId(),
        threadId,
      });
    } catch {
      toastManager.add({ type: "error", title: "No se pudo archivar el hilo" });
    }
  };

  return (
    <SidebarInset className="isolate h-dvh min-h-0 overflow-hidden bg-background">
      <div className="flex h-full flex-col">
        <div className="flex h-12 shrink-0 items-center gap-3 border-b border-border px-4 sm:px-6">
          <SidebarHeaderTrigger className="size-7 shrink-0" />
          <div className="flex items-center gap-2">
            <AlarmClockIcon className="size-4 text-muted-foreground/70" />
            <h1 className="text-sm font-semibold text-foreground">Pospuestos</h1>
          </div>
          {snoozed.length > 0 ? (
            <span className="ms-auto text-xs text-muted-foreground/72">
              {snoozed.length} {snoozed.length === 1 ? "hilo" : "hilos"}
            </span>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          {snoozed.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-20 text-center">
              <AlarmClockIcon className="size-8 text-muted-foreground/40" />
              <p className="text-sm font-medium text-foreground">No hay hilos pospuestos</p>
              <p className="max-w-sm text-xs text-muted-foreground/72">
                Cuando pospongas un hilo saldrá de tu lista y aparecerá aquí hasta que venza el
                plazo. Si necesita algo de ti antes, vuelve solo.
              </p>
            </div>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {snoozed.map((thread) => {
                const untilMs = Date.parse(thread.snoozedUntil ?? "");
                const projectName = projectNameById.get(thread.projectId) ?? null;
                // Archiving is a real thread command and needs the grant the
                // server checks; waking only touches this person's own row.
                const canArchive =
                  projectAssignments.get(thread.projectId)?.has("p.thread.use") ?? false;

                return (
                  <li
                    key={thread.id}
                    className="group flex items-center gap-3 rounded-lg border border-border/70 px-3 py-2.5 transition-colors hover:bg-accent/40"
                  >
                    <button
                      type="button"
                      className="flex min-w-0 flex-1 flex-col items-start gap-0.5 text-left"
                      onClick={() =>
                        void navigate({ to: "/$threadId", params: { threadId: thread.id } })
                      }
                    >
                      <span className="w-full truncate text-sm text-foreground">
                        {thread.title}
                      </span>
                      <span className="flex w-full items-center gap-1.5 truncate text-xs text-muted-foreground/72">
                        {projectName ? <span className="truncate">{projectName}</span> : null}
                        {projectName ? <span aria-hidden>·</span> : null}
                        {/* The countdown is the quick read; the exact instant is
                            what people check before bringing something back early. */}
                        <span className="shrink-0 text-foreground/80">
                          {formatSnoozeCountdown(untilMs, now)}
                        </span>
                        <span className="shrink-0">({formatSnoozeDeadline(untilMs, now)})</span>
                      </span>
                    </button>

                    <div className="flex shrink-0 items-center gap-1">
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => void wake(thread.id, thread.snoozedUntil ?? null)}
                      >
                        <Undo2Icon className="size-3.5" />
                        Volver a la lista
                      </Button>
                      {canArchive ? (
                        <Button
                          size="xs"
                          variant="ghost"
                          title="Archivar"
                          aria-label={`Archivar ${thread.title}`}
                          onClick={() => void archive(thread.id)}
                        >
                          <ArchiveIcon className="size-3.5" />
                        </Button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </SidebarInset>
  );
}
